import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMarketContext } from '../context/MarketDataContext';
import { getValidToken } from '../utils/auth';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '';

const SECTOR_COLORS = {
  'Information Technology': '#8b5cf6',
  'Health Care': '#06b6d4',
  'Financials': '#f59e0b',
  'Consumer Discretionary': '#ec4899',
  'Communication Services': '#3b82f6',
  'Industrials': '#6b7280',
  'Consumer Staples': '#10b981',
  'Energy': '#ef4444',
  'Utilities': '#a78bfa',
  'Real Estate': '#f97316',
  'Materials': '#14b8a6',
};

function getScoreColor(score) {
  if (score > 30) return '#22c55e';
  if (score > 10) return '#86efac';
  if (score > -10) return '#9ca3af';
  if (score > -30) return '#fca5a5';
  return '#ef4444';
}

function getScoreLabel(score) {
  if (score > 30) return 'Strong Bullish';
  if (score > 10) return 'Bullish';
  if (score > -10) return 'Neutral';
  if (score > -30) return 'Bearish';
  return 'Strong Bearish';
}

function computeScore(components, tickers, getOpen) {
  let rawScore = 0;
  let totalWeight = 0;
  let matchedCount = 0;
  const contribs = [];
  const sectorMap = {};

  for (const comp of components) {
    const t = tickers[comp.symbol];
    if (!t) continue;
    const openPrice = getOpen(t, comp);
    if (!openPrice || openPrice <= 0) continue;

    const changePercent = ((t.price - openPrice) / openPrice) * 100;
    const contribution = changePercent * comp.weight * 100;
    rawScore += contribution;
    totalWeight += comp.weight;
    matchedCount++;

    contribs.push({
      symbol: comp.symbol,
      company: comp.company_name,
      sector: comp.sector,
      weight: comp.weight,
      changePercent,
      contribution,
      price: t.price,
    });

    if (!sectorMap[comp.sector]) {
      sectorMap[comp.sector] = { sector: comp.sector, contribution: 0, count: 0, matched: 0 };
    }
    sectorMap[comp.sector].contribution += contribution;
    sectorMap[comp.sector].matched++;
  }

  for (const comp of components) {
    if (!sectorMap[comp.sector]) {
      sectorMap[comp.sector] = { sector: comp.sector, contribution: 0, count: 0, matched: 0 };
    }
    sectorMap[comp.sector].count++;
  }

  const normalizedScore = totalWeight > 0
    ? Math.max(-100, Math.min(100, rawScore / totalWeight))
    : 0;

  contribs.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
  const sectors = Object.values(sectorMap)
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

  return {
    score: normalizedScore,
    matchedCount,
    contributors: contribs.slice(0, 20),
    sectorBreakdown: sectors,
  };
}

function formatChartTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Score card component
function ScoreCard({ title, score, matchedCount }) {
  const color = getScoreColor(score);
  const label = getScoreLabel(score);
  return (
    <div className="bg-gray-900 rounded-2xl p-6 text-center flex flex-col items-center justify-center">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">{title}</p>
      <div className="relative mb-3">
        <div className="text-6xl font-bold tabular-nums" style={{ color }}>
          {matchedCount > 0 ? (score >= 0 ? '+' : '') + score.toFixed(1) : '—'}
        </div>
      </div>
      <div
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold"
        style={{ backgroundColor: color + '20', color }}
      >
        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        {matchedCount > 0 ? label : 'Waiting for data'}
      </div>
      <div className="w-full mt-5">
        <div className="relative h-2 rounded-full bg-gray-800 overflow-hidden">
          <div
            className="absolute top-0 h-full rounded-full transition-all duration-500"
            style={{
              backgroundColor: color,
              left: `${Math.min(50, 50 + (score / 2))}%`,
              width: `${Math.abs(score / 2)}%`,
            }}
          />
          <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gray-600" />
        </div>
        <div className="flex justify-between mt-1 text-[10px] text-gray-500">
          <span>-100</span>
          <span>0</span>
          <span>+100</span>
        </div>
      </div>
    </div>
  );
}

// Score chart component — fed from backend history
function ScoreChart({ title, data, dataKey, gradientId }) {
  const lastScore = data.length > 0 ? (data[data.length - 1][dataKey] ?? 0) : 0;
  const color = getScoreColor(lastScore);
  return (
    <div className="lg:col-span-2 bg-gray-900 rounded-2xl p-5">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">{title}</p>
      {data.length < 2 ? (
        <div className="h-40 flex items-center justify-center text-gray-500 text-sm">
          Collecting data points...
        </div>
      ) : (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#9ca3af' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => [v != null ? v.toFixed(2) : '—', 'Score']}
              />
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                fill={`url(#${gradientId})`}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function SP500Momentum() {
  const { tickers } = useMarketContext();
  const [components, setComponents] = useState([]);
  const [sessionOpens, setSessionOpens] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Backend-stored score histories
  const [scoreHistory, setScoreHistory] = useState([]);

  // Load components, session opens, and score history
  const fetchData = useCallback(async (initial) => {
    try {
      const token = await getValidToken();
      const fetches = [
        fetch(`${API_URL}/api/sp500/score-history`, { headers: { Authorization: `Bearer ${token}` } }),
      ];
      if (initial) {
        fetches.push(
          fetch(`${API_URL}/api/sp500/components`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/sp500/session-opens`, { headers: { Authorization: `Bearer ${token}` } }),
        );
      }
      const results = await Promise.all(fetches);

      // Score history
      if (results[0].ok) {
        const historyData = await results[0].json();
        const now = new Date();
        const oneHourAgo = now.getTime() - 3600000;
        // Get 4 AM ET and 9:30 AM ET as UTC timestamps for filtering
        const etDateStr = now.toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
        const [em, ed, ey] = etDateStr.split('/');

        const formatted = (historyData || []).map(p => {
          const ts = new Date(p.time).getTime();
          return {
            ...p,
            ts,
            time: formatChartTime(p.time),
          };
        });

        // Compute ET boundaries for chart filtering
        // Use Intl to get the actual ET offset
        const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        const etOffset = now.getTime() - etNow.getTime();
        const premarketStart = new Date(`${ey}-${em}-${ed}T04:00:00`).getTime() + etOffset;
        const mainStart = new Date(`${ey}-${em}-${ed}T09:30:00`).getTime() + etOffset;

        setScoreHistory({
          all: formatted,
          hour: formatted.filter(p => p.ts > oneHourAgo),
          premarket: formatted.filter(p => p.ts >= premarketStart),
          main: formatted.filter(p => p.ts >= mainStart),
        });
      }

      // Initial data loads
      if (initial) {
        if (results[1] && results[1].ok) {
          const compData = await results[1].json();
          setComponents(compData);
        }
        if (results[2] && results[2].ok) {
          const opensData = await results[2].json();
          const opensMap = {};
          for (const o of opensData) {
            opensMap[o.symbol] = { premarket_open: o.premarket_open, main_open: o.main_open };
          }
          setSessionOpens(opensMap);
        }
      }
    } catch (err) {
      if (initial) setError(err.message);
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Refresh score history every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getPremarketOpen = useCallback((ticker, comp) => {
    if (ticker.premarketOpen > 0) return ticker.premarketOpen;
    const dbOpen = sessionOpens[comp.symbol];
    if (dbOpen && dbOpen.premarket_open > 0) return dbOpen.premarket_open;
    return 0;
  }, [sessionOpens]);

  const getMainOpen = useCallback((ticker, comp) => {
    if (ticker.mainOpen > 0) return ticker.mainOpen;
    const dbOpen = sessionOpens[comp.symbol];
    if (dbOpen && dbOpen.main_open > 0) return dbOpen.main_open;
    return 0;
  }, [sessionOpens]);

  // Live scores (computed from WebSocket for the score cards)
  const pmScore = useMemo(() => computeScore(components, tickers, getPremarketOpen), [components, tickers, getPremarketOpen]);
  const mmScore = useMemo(() => computeScore(components, tickers, getMainOpen), [components, tickers, getMainOpen]);

  const hourData = scoreHistory.hour || [];
  const pmData = scoreHistory.premarket || [];
  const mmData = scoreHistory.main || [];

  // Hour score comes from backend (uses true 1-hour-ago price as baseline)
  const hourScore = useMemo(() => {
    if (hourData.length === 0) return { score: 0, matchedCount: 0 };
    const last = hourData[hourData.length - 1];
    return {
      score: last.hour_score ?? 0,
      matchedCount: last.hour_matched ?? 0,
    };
  }, [hourData]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">S&P 500 Momentum</h1>
        <div className="text-gray-500">Loading components...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">S&P 500 Momentum</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  const primaryScore = pmScore.matchedCount > 0 ? pmScore : hourScore;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">S&P 500 Momentum</h1>
          <p className="text-sm text-gray-500 mt-1">
            {primaryScore.matchedCount} / {components.length} components with live data
          </p>
        </div>
      </div>

      {/* Row 1: Last 1 Hour */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <ScoreCard
          title="Weighted Direction Score — LAST 1 HOUR"
          score={hourScore.score}
          matchedCount={hourScore.matchedCount}
        />
        <ScoreChart
          title="Score — Last 1 Hour"
          data={hourData}
          dataKey="hour_score"
          gradientId="hourGrad"
        />
      </div>

      {/* Row 2: Pre-market */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <ScoreCard
          title="Weighted Pre-market Direction Score"
          score={pmScore.score}
          matchedCount={pmScore.matchedCount}
        />
        <ScoreChart
          title="Score — From Pre-market"
          data={pmData}
          dataKey="premarket_score"
          gradientId="pmGrad"
        />
      </div>

      {/* Row 3: Main-market */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <ScoreCard
          title="Weighted Main-market Direction Score"
          score={mmScore.score}
          matchedCount={mmScore.matchedCount}
        />
        <ScoreChart
          title="Score — From Main-market"
          data={mmData}
          dataKey="main_score"
          gradientId="mmGrad"
        />
      </div>

      {/* Sector Breakdown + Top Contributors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">Sector Breakdown</p>
          {primaryScore.matchedCount === 0 ? (
            <div className="text-gray-500 text-sm text-center py-8">Waiting for market data...</div>
          ) : (
            <div className="space-y-2.5">
              {primaryScore.sectorBreakdown.map((s) => {
                const color = SECTOR_COLORS[s.sector] || '#6b7280';
                const isPositive = s.contribution >= 0;
                const maxContrib = Math.max(...primaryScore.sectorBreakdown.map(x => Math.abs(x.contribution)), 0.01);
                const barWidth = Math.abs(s.contribution) / maxContrib * 100;
                return (
                  <div key={s.sector} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
                        <span className="text-sm text-gray-300">{s.sector}</span>
                        <span className="text-[10px] text-gray-500">{s.matched}/{s.count}</span>
                      </div>
                      <span className={`text-sm font-semibold tabular-nums ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                        {isPositive ? '+' : ''}{s.contribution.toFixed(2)}
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: isPositive ? '#22c55e' : '#ef4444',
                          opacity: 0.7,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">Top 20 Contributors</p>
          {primaryScore.matchedCount === 0 ? (
            <div className="text-gray-500 text-sm text-center py-8">Waiting for market data...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                    <th className="pb-2">Symbol</th>
                    <th className="pb-2 text-right">Weight</th>
                    <th className="pb-2 text-right">Change</th>
                    <th className="pb-2 text-right">Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {primaryScore.contributors.map((c) => {
                    const isPositive = c.contribution >= 0;
                    return (
                      <tr
                        key={c.symbol}
                        className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                        onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${c.symbol}&interval=1`, '_blank', 'noopener')}
                      >
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-1 h-4 rounded-full"
                              style={{ backgroundColor: isPositive ? '#22c55e' : '#ef4444' }}
                            />
                            <div>
                              <span className="font-semibold text-gray-200">{c.symbol}</span>
                              <span className="text-[10px] text-gray-500 ml-1.5 hidden sm:inline">{c.sector}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 text-right text-gray-400 tabular-nums">
                          {(c.weight * 100).toFixed(2)}%
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          <span className={isPositive ? 'text-green-400' : 'text-red-400'}>
                            {c.changePercent >= 0 ? '+' : ''}{c.changePercent.toFixed(2)}%
                          </span>
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          <span className={`font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                            {isPositive ? '+' : ''}{c.contribution.toFixed(3)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
